import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import { type FC, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { PuffLoader } from 'react-spinners';

import { Message } from '@/shared/components/ui/Message';
import { formatNumber, formatPrice } from '@/shared/utils/format';

// Enregistrer les composants Chart.js nécessaires
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface PriceDistributionCurveProps {
  buckets: { price: number; count: number }[] | null;
  priceMin: number | null;
  priceMax: number | null;
  loading?: boolean;
}

export const PriceDistributionCurve: FC<PriceDistributionCurveProps> = ({ buckets, priceMin, loading }) => {
  const chartRef = useRef<ChartJS<'line'>>(null);

  const containerStyles = css({
    w: 'full',
    bg: 'base.segBg',
    border: '0.75px solid',
    borderColor: 'base.border',
    borderRadius: '0.625rem',
    position: 'relative',
  });

  const contentStyles = css({
    p: '1.5625rem',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  });

  if (loading) {
    return (
      <div className={containerStyles}>
        <div className={contentStyles}>
          <div className={center()}>
            <PuffLoader color={token.var('colors.accent.primary')} size={40} />
          </div>
        </div>
      </div>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <div className={containerStyles}>
        <div className={contentStyles}>
          <Message type="info" bordered>
            Aucune donnée de distribution disponible
          </Message>
        </div>
      </div>
    );
  }

  // Préparer les données pour le graphique
  const chartData = buckets.map((bucket) => ({
    price: bucket.price,
    count: bucket.count,
  }));

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 0,
      },
    },
    scales: {
      x: {
        type: 'category',
        grid: {
          color: '#222328',
          lineWidth: 1,
          display: true,
        },
        border: {
          color: '#222328',
          width: 1,
        },
        ticks: {
          color: '#b3b5bd',
          font: {
            family: 'Manrope, sans-serif',
            size: 11,
          },
          callback: function (_value, index) {
            return formatPrice(chartData[index].price);
          },
        },
      },
      y: {
        type: 'linear',
        beginAtZero: true,
        grid: {
          color: '#222328',
          lineWidth: 1,
          display: true,
        },
        border: {
          color: '#222328',
          width: 1,
        },
        ticks: {
          color: '#4f5055',
          font: {
            family: 'Manrope, sans-serif',
            size: 11,
            weight: 'bold',
          },
          callback: function (value: any) {
            return formatNumber(value);
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#16171b',
        borderColor: '#1e1f24',
        borderWidth: 0.75,
        cornerRadius: 6,
        titleColor: '#e6e6e9',
        bodyColor: '#b3b5bd',
        titleFont: {
          family: 'Manrope, sans-serif',
          size: 11,
        },
        bodyFont: {
          family: 'Manrope, sans-serif',
          size: 11,
        },
        padding: 8,
        displayColors: false,
        callbacks: {
          title: (tooltipItems: TooltipItem<'line'>[]) => {
            const index = tooltipItems[0].dataIndex;
            const maxPrice = chartData[index].price;
            const minPrice = index === 0 ? (priceMin ?? 0) : (buckets[index - 1]?.price ?? 0);
            return `Prix entre ${formatPrice(minPrice)} et ${formatPrice(maxPrice)}`;
          },
          label: (tooltipItem: TooltipItem<'line'>) => {
            return `Nombre d'articles: ${formatNumber(chartData[tooltipItem.dataIndex].count)}`;
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0.4, // Courbe lisse
        borderWidth: 2,
        borderColor: 'rgba(94, 105, 211, 1)',
      },
      point: {
        radius: 4,
        backgroundColor: 'rgba(94, 105, 211, 1)',
        borderColor: '#16171b',
        borderWidth: 2,
        hoverRadius: 6,
        hoverBorderWidth: 2,
      },
    },
  };

  const chartDataConfig: ChartData<'line'> = {
    labels: chartData.map((item) => formatPrice(item.price)),
    datasets: [
      {
        data: chartData.map((item) => item.count),
        fill: true,
        backgroundColor: 'rgba(94, 105, 211, 0.1)',
        borderColor: 'rgba(94, 105, 211, 1)',
        pointBackgroundColor: 'rgba(94, 105, 211, 1)',
        pointBorderColor: '#16171b',
        pointHoverBackgroundColor: 'rgba(94, 105, 211, 1)',
        pointHoverBorderColor: '#16171b',
      },
    ],
  };

  const chartHeight = 292;

  return (
    <div className={containerStyles}>
      <div className={contentStyles}>
        <div style={{ height: `${chartHeight}px` }}>
          <Line ref={chartRef} data={chartDataConfig} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};
